export const GUIDE_TIP_TITLE = 'Mario，你的专属 AI 助手';
import img2 from '@src/assets/claw/tips/2.png';
import img5 from '@src/assets/claw/tips/5.png';
import img6 from '@src/assets/claw/tips/6.png';
import img7 from '@src/assets/claw/tips/7.png';
import img8 from '@src/assets/claw/tips/8.png';

// ─── Node Types ─────────────────────────────────────────────────────────────

export interface TextNode {
  type: 'text';
  content: string;
}

export interface SelectNode {
  type: 'select';
  key: string;
  placeholder: string;
  options: string[];
  defaultValue?: string;
}

export interface DatetimeNode {
  type: 'datetime';
  key: string;
  placeholder: string;
  defaultValue?: string;
}

export interface InputNode {
  type: 'input';
  key: string;
  placeholder: string;
  defaultValue?: string;
}

export interface MentionNode {
  type: 'mention';
  key: string;
  placeholder: string;
  /** 明确指定搜索范围；不填时默认人群混合搜索 */
  mentionType?: 'user' | 'group' | 'usergroup';
  defaultValue?: string;
}

export type TemplateNode =
  | TextNode
  | SelectNode
  | DatetimeNode
  | InputNode
  | MentionNode;

// ─── Template ────────────────────────────────────────────────────────────────

export interface ChatboxTemplate {
  id: string;
  name: string;
  nodes: TemplateNode[];
}

export const GUIDE_TIP_DESC =
  '聊天、查资料、写内容、做图表，都交给我。\n选个场景开始，或直接告诉我你想做什么。';

export interface GuideTipItem {
  id: string;
  label: string;
  icon: string;
  type?: 'suggestion' | 'template' | 'prompt' | 'placeholder';
  suggestions?: { content: string; value: string }[];
  template?: TemplateNode[];
  prompt?: string;
  placeholder?: string;
}

export const GUIDE_TIP_ITEMS: GuideTipItem[] = [
  {
    id: 'introduce',
    label: '嘿！新朋友看这里',
    icon: img7,
    type: 'suggestion',
    suggestions: [
      {
        content: '做个自我介绍',
        value: '做个自我介绍',
      },
      {
        content: '什么是 Skills (技能)，可以怎么添加和使用',
        value: '什么是 Skills (技能)，可以怎么添加和使用',
      },
      {
        content: '用的什么模型，该如何切换',
        value: '用的什么模型，该如何切换',
      },
      {
        content: '遇到 Mario 的使用问题，也可以直接问你吗？',
        value: '遇到 Mario 的使用问题，也可以直接问你吗？',
      },
    ],
  },
  {
    id: 'researcher',
    label: '帮我准备材料',
    icon: img6,
    type: 'suggestion',
    suggestions: [
      {
        content: '生成技术调研的 PDF 文件',
        value:
          '帮我生成一份「对比 OpenClaw 与业内主流 Agent 架构的技术调研方案」，生成为 PDF 文件',
      },
      {
        content: '生成用户分群分析 Excel 文档',
        value: `生成用户分群分析 Excel 表格，包含以下三个分群维度：

1. 用户生命周期
   - 新用户（注册<7 天）
   - 成长用户（7-30 天）
   - 成熟用户（30-180 天）
   - 衰退用户（180 天 + 未登录）

2. 城市等级
   - 一线城市
   - 二线城市
   - 三线及以下

3. 行为特征
   - 内容消费者（只看视频）
   - 内容创作者（发布视频）
   - 直播观众
   - 电商买家

每个分群需要包含以下指标：
- 用户规模
- DAU/MAU
- 人均时长
- 留存率
- 付费率

输出要求：
- 单个 Sheet 整合所有维度数据（不要多 Sheet）
- 内置可视化图表（柱状图/折线图/饼图等，展示各维度关键指标对比）
- 支持筛选查看（表头添加筛选功能，可按维度/指标自由筛选）
- 数据格式清晰，关键指标高亮显示`,
      },
      {
        content: '帮我做一个产品发布会介绍的 PPT',
        value: `帮我生成一个 Openclaw 产品发布会介绍的 PPT，包含封面、痛点、解决方案、商业模式、团队介绍，蓝色专业风格，约 12 页
适合：正式汇报、融资路演、会议文档
输出：.pptx 文件`,
      },
      {
        content: '生成内容审核规范 Word 文档',
        value: `生成内容审核规范 Word 文档：

适用范围：快手平台所有上传视频

审核标准：
1. 禁止内容
   - 政治敏感
   - 色情低俗
   - 暴力恐怖
   - 虚假宣传
   - 侵权内容

2. 限流内容
   - 过度暴露
   - 危险行为
   - 炫富拜金
   - 低质营销

3. 推荐标准
   - 原创优先
   - 正能量
   - 高质量制作
   - 用户互动好

4. 处罚规则
   - 轻度违规：限流、下架
   - 中度违规：封号 7-30 天
   - 重度违规：永久封禁

输出要求：
- Word 文档
- 正式规范格式
- 方便审核团队培训使用`,
      },
    ],
  },
  {
    id: 'schedule',
    label: '帮我拉个会',
    icon: img5,
    type: 'template',
    template: [
      { type: 'text', content: '帮我预约' },
      {
        type: 'datetime',
        key: 'time',
        placeholder: '明天下午 03:00',
      },
      { type: 'text', content: '在' },
      {
        type: 'select',
        key: 'room',
        placeholder: '元中心',
        options: [
          '北京-元中心T1',
          '北京-元中心T2',
          '北京-元中心T3',
          '北京-元中心T9',
          '北京-元中心T10',
          '北京-元中心T11',
          '北京-元中心T12',
          '北京-元中心T13',
          '北京-万家灯火大厦【F1-F2】',
          '北京-万家灯火大厦【F3-F6】',
          '北京-万家灯火大厦【F7-F8】',
          '北京-中黎科技园1号楼',
          '天津-新城市中心',
          '铜仁-万山',
          '成都-海思科6号楼',
          '成都-海思科7号楼',
          '吉首-湘金B座',
          '无锡-恒隆广场',
          '成都-新川',
          '广州-广电平云大厦',
          '武汉-融创智谷',
          '深圳-百度国际大厦',
          '淮安-洪泽区电子商务产业园',
          '无锡-国金中心',
          '杭州-EFC欧美金融中心T6',
          '杭州-星耀',
          '上海-星联科技园1号楼',
          '上海-星联科技园2号楼',
          '珠海-华发',
          '海口-沃克公园',
          'Jakarta-The Plaza',
          'Sao Paulo-SP Corporate Tower',
        ],
      },
      { type: 'text', content: '会议室的会议，参与人是' },
      {
        type: 'mention',
        key: 'members',
        placeholder: '@搜索成员',
        mentionType: 'usergroup',
      },
      { type: 'text', content: '主题是' },
      {
        type: 'input',
        key: 'topic',
        placeholder: '请输入主题内容',
      },
    ],
  },
  {
    id: 'crawler',
    label: '看看群聊重点',
    icon: img2,
    type: 'template',
    template: [
      { type: 'text', content: '帮我获取' },
      {
        type: 'mention',
        key: 'group',
        placeholder: '@搜索群聊',
        mentionType: 'group',
      },
      { type: 'text', content: 'Kim 群的内容，并生成个人待办事项' },
    ],
  },
  // {
  //   id: 'trending',
  //   label: '群-捕风追影',
  //   icon: '🔥',
  //   type: 'template',
  //   template: [
  //     { type: 'text', content: '监控' },
  //     { type: 'mention', key: 'group', placeholder: '@搜索群聊' },
  //     { type: 'text', content: '如果出现' },
  //     { type: 'input', key: 'keyword', placeholder: '请输入关键词' },
  //     { type: 'text', content: '等关键词，立即提醒我' },
  //   ],
  // },
  {
    id: 'joingroup',
    label: '参加 Agent 们的社区',
    icon: img8,
    type: 'template',
    template: [
      { type: 'text', content: `帮我看看 Kim 的 Agent 聊天室里的最新讨论内容` },
      { type: 'text', content: `帮我看看 Kimobook 社区内大家在聊什么` },
    ],
  },
];
